import { Platform } from '@angular/cdk/platform';
import { ComponentType } from '@angular/cdk/portal';
import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { App } from '@df/core/app';
import { Acl } from '@df/core/service/acl';
import { ModalConfig, TModalPosition } from '@df/ui/modal/modal-config';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { ModalService } from '@df/ui/modal/modal.service';
import { concat, filter, first, interval } from 'rxjs';
import { getDocument } from 'ssr-window';
import { NPwa } from '../pwa.interface';
import { PwaUpdateComponent } from './pwa-update.component';

@Injectable({
    providedIn: 'root'
})
export class PwaUpdateService {
    /** @public */
    modalPlatform: NPwa.EPlatform | undefined;

    /** @internal */
    protected modalRef?: ModalRef<PwaUpdateComponent>;

    constructor(
        protected app: App,
        protected appRef: ApplicationRef,
        protected modalService: ModalService,
        protected platform: Platform,
        protected swUpdate: SwUpdate
    ) {}

    init(): void {
        if (this.swUpdate.isEnabled) {
            if (this.platform.ANDROID) {
                this.modalPlatform = NPwa.EPlatform.ANDROID;
            } else if (this.platform.IOS && this.platform.SAFARI) {
                this.modalPlatform = NPwa.EPlatform.IOS;
            }

            if (this.app.isPlatformBrowser) {
                this.setUpdateCheckInterval();

                if (Acl.isProdMode()) {
                    this.updateIfUserWants();
                } else {
                    this.updateIfPossible();
                }

                this.watchForUnrecoverableState();

                this.logUpdates();
            }
        }
    }

    setUpdateCheckInterval(): void {
        // Allow the app to stabilize first, before starting
        // polling for updates with `interval()`.
        const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
        const everySixHours$ = interval(6 * 60 * 60 * 1000);
        const everySixHoursOnceAppIsStable$ = concat(appIsStable$, everySixHours$);

        everySixHoursOnceAppIsStable$.subscribe(() => this.swUpdate.checkForUpdate());
    }

    updateIfPossible(): void {
        this.swUpdate.versionUpdates.pipe(filter(versionEvent => versionEvent.type === 'VERSION_READY')).subscribe(() =>
            this.swUpdate.activateUpdate().then(() => {
                // !!! Change for production
                // this.documentReload();
                this.modalOpen();
            })
        );
    }

    updateIfUserWants(): void {
        this.swUpdate.versionUpdates
            .pipe(filter(versionEvent => versionEvent.type === 'VERSION_READY'))
            .subscribe((event: VersionReadyEvent) => {
                this.app.logger.info(`[PWA] currentVersion=[${event.currentVersion} | latestVersion=[${event.latestVersion}]`);

                this.modalOpen();
            });
    }

    watchForUnrecoverableState(): void {
        this.swUpdate.unrecoverable.subscribe(e => {
            alert(
                `The code got jumbled by a progressive web app update; if you see this, please let us know! We must reload the page to regain integrity. This may be a cryptic error: ${e.reason}`
            );

            this.documentReload();
        });
    }

    logUpdates(): void {
        this.swUpdate.versionUpdates.subscribe(event => this.app.logger.info(event));
    }

    modalOpen(): void {
        this.modalRef = this.modalService.open(this.modalComponent(), this.modalConfig(), this.modalPosition());
    }

    modalClose(): void {
        this.modalRef?.close();
    }

    modalComponent(): ComponentType<PwaUpdateComponent> {
        return PwaUpdateComponent;
    }

    modalConfig(): Partial<ModalConfig> {
        return {
            hasBackdrop: false,
            maxWidth: '100%',
            minWidth: '100%',
            modalClass: 'p-a-0 center pe-none pwa-update-modal',
            position: {
                right: '0',
                bottom: '0',
                left: '0'
            }
        };
    }

    modalPosition(): TModalPosition {
        return 'bottom';
    }

    reload(): void {
        if (this.app.isPlatformBrowser) {
            this.swUpdate.activateUpdate().then(() => this.documentReload());
        }
    }

    documentReload(): void {
        const document = getDocument();

        document.location.reload();
    }
}
